import type { IntakeSpec, FurnitureCabinetDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';
import { HINGE_CUP, METRIC_SCREWS } from '@shared/hardwareCatalog';

function getDetails(intake: IntakeSpec): FurnitureCabinetDetails {
  return intake.details as FurnitureCabinetDetails;
}

const PANEL_T = 18;
const BACK_T = 6;

type CabinetDims = { width: number; depth: number; height: number };

function getDefaultDims(cabinetType: FurnitureCabinetDetails['cabinetType']): CabinetDims {
  switch (cabinetType) {
    case 'base':   return { width: 600, depth: 500, height: 850 };
    case 'wall':   return { width: 600, depth: 320, height: 700 };
    case 'tall':   return { width: 600, depth: 500, height: 2000 };
    case 'corner': return { width: 900, depth: 900, height: 850 };
    case 'island': return { width: 1200, depth: 600, height: 900 };
  }
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  // cup_hinge handling moved to features() — emit real pocket + screw_hole features.
  //

  const dims = getDefaultDims(d.cabinetType);
  const cW = intake.core.size?.width ?? dims.width;
  const cD = intake.core.size?.length ?? dims.depth;
  const cH = intake.core.size?.height ?? dims.height;
  const innerW = cW - 2 * PANEL_T;

  const out: Part[] = [];

  // 2 side panels (left/right)
  out.push(
    {
      id: 'side_left',
      name: '側板（左）',
      kind: 'panel',
      materialId: 'mat-primary',
      width: cH,
      depth: cD,
      thickness: PANEL_T,
      position: [-(cW / 2), 0, cH / 2],
      rotation: [0, 0, 0],
      tags: ['side', 'left'],
      paramRefs: [],
      cutouts: [],
    },
    {
      id: 'side_right',
      name: '側板（右）',
      kind: 'panel',
      materialId: 'mat-primary',
      width: cH,
      depth: cD,
      thickness: PANEL_T,
      position: [cW / 2, 0, cH / 2],
      rotation: [0, 0, 0],
      tags: ['side', 'right'],
      paramRefs: [],
      cutouts: [],
    },
  );

  // 1 top panel
  out.push({
    id: 'top',
    name: '天板',
    kind: 'panel',
    materialId: 'mat-primary',
    width: innerW,
    depth: cD,
    thickness: PANEL_T,
    position: [0, 0, cH - PANEL_T / 2],
    rotation: [0, 0, 0],
    tags: ['top'],
    paramRefs: [],
    cutouts: [],
  });

  // 1 bottom panel
  out.push({
    id: 'bottom',
    name: '底板',
    kind: 'panel',
    materialId: 'mat-primary',
    width: innerW,
    depth: cD,
    thickness: PANEL_T,
    position: [0, 0, PANEL_T / 2],
    rotation: [0, 0, 0],
    tags: ['bottom'],
    paramRefs: [],
    cutouts: [],
  });

  // 1 back panel
  out.push({
    id: 'back',
    name: '背板',
    kind: 'panel',
    materialId: 'mat-primary',
    width: cW,
    depth: cH,
    thickness: BACK_T,
    position: [0, cD / 2, cH / 2],
    rotation: [90, 0, 0],
    tags: ['back'],
    paramRefs: [],
    cutouts: [],
  });

  // Door panels
  const doorCount = d.doorCount;
  if (doorCount > 0) {
    const doorW = innerW / doorCount;
    for (let i = 0; i < doorCount; i++) {
      const xPos = -(innerW / 2) + doorW * i + doorW / 2;
      out.push({
        id: `door_${i + 1}`,
        name: `扉 ${i + 1}`,
        kind: 'panel',
        materialId: 'mat-primary',
        width: doorW,
        depth: PANEL_T,
        thickness: cH - 2 * PANEL_T,
        position: [xPos, -(cD / 2), PANEL_T + (cH - 2 * PANEL_T) / 2],
        rotation: [0, 0, 0],
        tags: ['door'],
        paramRefs: [],
        cutouts: [],
      });
    }
  }

  // Drawer fronts
  const drawerCount = d.drawerCount;
  if (drawerCount > 0) {
    const drawerH = (cH - 2 * PANEL_T) / (drawerCount + 1);
    for (let i = 0; i < drawerCount; i++) {
      out.push({
        id: `drawer_front_${i + 1}`,
        name: `引出し前板 ${i + 1}`,
        kind: 'panel',
        materialId: 'mat-primary',
        width: innerW,
        depth: PANEL_T,
        thickness: drawerH - 5,
        position: [0, -(cD / 2), PANEL_T + drawerH * (i + 1) - drawerH / 2],
        rotation: [0, 0, 0],
        tags: ['drawer_front'],
        paramRefs: [],
        cutouts: [],
      });
    }
  }

  // Internal shelves
  const shelvesInside = d.shelvesInside;
  if (shelvesInside > 0) {
    const shelfSpacing = (cH - 2 * PANEL_T) / (shelvesInside + 1);
    for (let i = 0; i < shelvesInside; i++) {
      const zPos = PANEL_T + shelfSpacing * (i + 1);
      out.push({
        id: `shelf_${i + 1}`,
        name: `棚板 ${i + 1}`,
        kind: 'panel',
        materialId: 'mat-primary',
        width: innerW,
        depth: cD - BACK_T - 5,
        thickness: PANEL_T,
        position: [0, 0, zPos],
        rotation: [0, 0, 0],
        tags: ['shelf'],
        paramRefs: [],
        cutouts: [],
      });
    }
  }

  return out;
}

export function joints(intake: IntakeSpec): Joint[] {
  const d = getDetails(intake);
  let joinery: string = d.joineryType;

  // cam_lock: no dedicated joint kind in B2 core / B2 rest. Treat as 'screw' for
  // V1 seed; agent can refine in conversation.
  if (joinery === 'cam_lock') joinery = 'screw';

  // pocket_screw → treat as screw
  const effectiveJoinery = joinery === 'pocket_screw' ? 'screw' : joinery;

  const out: Joint[] = [];

  // side→top and side→bottom joints (4 total)
  const topBottomPairs: Array<[string, string, string]> = [
    ['top_to_left',    'top',    'side_left'],
    ['top_to_right',   'top',    'side_right'],
    ['bottom_to_left', 'bottom', 'side_left'],
    ['bottom_to_right','bottom', 'side_right'],
  ];

  const dims = getDefaultDims(d.cabinetType);
  const cD = intake.core.size?.length ?? dims.depth;

  for (const [jId, fromId, toId] of topBottomPairs) {
    if (effectiveJoinery === 'dado') {
      out.push({ id: jId, fromPartId: fromId, toPartId: toId, type: 'dado', width: PANEL_T, depth: 6, length: cD });
    } else if (effectiveJoinery === 'dowel') {
      out.push({ id: jId, fromPartId: fromId, toPartId: toId, type: 'dowel', diameter: 8, length: 30, count: 4 });
    } else {
      // screw (also covers pocket_screw)
      out.push({ id: jId, fromPartId: fromId, toPartId: toId, type: 'screw', size: 'M5', length: 35, count: 4 });
    }
  }

  // dado for back panel: needs `rabbet` joint kind, deferred to B2 rest.
  // V1 emits screw joints for back-to-side; agent can refine.
  //
  out.push(
    { id: 'back_to_left',  fromPartId: 'back', toPartId: 'side_left',  type: 'screw', size: 'M4', length: 20, count: 6 },
    { id: 'back_to_right', fromPartId: 'back', toPartId: 'side_right', type: 'screw', size: 'M4', length: 20, count: 6 },
  );

  return out;
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const dims = getDefaultDims(d.cabinetType);
  const cH = intake.core.size?.height ?? dims.height;
  const panelT = PANEL_T;
  const isCupHinge = d.hardwareStyle === 'cup_hinge';

  const out: Feature[] = [];

  // Edge chamfers on top, sides, and door edges
  out.push(
    {
      id: 'chamfer_top',
      partId: 'top',
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 1,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    },
    {
      id: 'chamfer_side_left',
      partId: 'side_left',
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 1,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    },
    {
      id: 'chamfer_side_right',
      partId: 'side_right',
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 1,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    },
  );

  const doorCount = d.doorCount;
  const hingesPerDoor = d.cabinetType === 'tall' ? 3 : 2;
  const cupEdgeOffset = HINGE_CUP.cupEdgeOffsetMin + HINGE_CUP.cupDiameter / 2 + 4; // ~22mm typical
  const plateScrewSpacing = HINGE_CUP.plateScrewSpacing;

  for (let i = 0; i < doorCount; i++) {
    const doorId = `door_${i + 1}`;
    const doorH = cH - 2 * panelT;

    // Edge chamfer on door
    out.push({
      id: `chamfer_${doorId}`,
      partId: doorId,
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 1,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });

    // Hinge centerline z positions (top + bottom, plus middle for tall doors)
    const hingeZOffsets: number[] = [100, doorH - 100];
    if (hingesPerDoor === 3) {
      hingeZOffsets.push(doorH / 2);
    }

    if (isCupHinge) {
      // Cup hinges: 35mm cylindrical pocket on door inner face for each hinge.
      // Plate screw holes on the carcass side panel paired with each cup,
      // spaced by HINGE_CUP.plateScrewSpacing (32mm).
      const cupLabels = ['top', 'bot', 'mid'];
      for (let h = 0; h < hingesPerDoor; h++) {
        const z = hingeZOffsets[h];
        const label = cupLabels[h] ?? `h${h + 1}`;
        out.push({
          id: `cup-${i + 1}-${label}`,
          partId: doorId,
          kind: 'pocket',
          shape: 'cylindrical',
          diameter: HINGE_CUP.cupDiameter,
          depth: HINGE_CUP.cupDepth,
          position: [cupEdgeOffset, 0, z],
          rotation: [0, 0, 0],
          axis: 'y',
          bottom: 'flat',
          purpose: 'hinge_cup',
          catalogSource: 'HINGE_CUP.cupDiameter',
          callout: { label: 'ヒンジカップ ⌀35×13', sectionDetailHint: 'detail-view-needed' },
          paramRefs: [],
        });

        // Carcass plate screw holes (M4×16 pan-head), placed on appropriate side
        // panel — door 1 → left, door 2 → right, door 3+ → alternates.
        const sideId = i % 2 === 0 ? 'side_left' : 'side_right';
        const screwLen = 16;
        const screwSize = HINGE_CUP.plateScrewSize;
        const headStyle = 'pan' as const;
        for (let p = 0; p < 2; p++) {
          const offset = (p === 0 ? -1 : 1) * (plateScrewSpacing / 2);
          out.push({
            id: `hinge-plate-${i + 1}-${label}-${p + 1}`,
            partId: sideId,
            kind: 'screw_hole',
            size: screwSize,
            length: screwLen,
            head: headStyle,
            axis: 'x',
            thread: false,
            teardrop: false,
            holeOversize: METRIC_SCREWS[screwSize].clearanceHole - METRIC_SCREWS[screwSize].nominal,
            position: [0, 0, z + offset],
            rotation: [0, 0, 0],
            paramRefs: [],
          });
        }
      }
    } else {
      // butt_hinge / piano_hinge / concealed_hinge: simple hinge mounting holes only.
      for (let h = 0; h < hingesPerDoor; h++) {
        out.push({
          id: `hinge_hole_door${i + 1}_h${h + 1}`,
          partId: doorId,
          kind: 'screw_hole',
          size: 'M3',
          length: panelT,
          head: 'pan',
          axis: 'y',
          thread: false,
          teardrop: false,
          position: [-(panelT / 2), 0, hingeZOffsets[h] ?? doorH / 2],
          rotation: [0, 0, 0],
          paramRefs: [],
        });
      }
    }
  }

  return out;
}
